import { Suspense, useEffect, useRef, useState } from 'react';
import { Router } from 'wouter';
import { memoryLocation } from 'wouter/memory-location';
import { AppRoutes } from '../../AppRoutes.js';
import { BackIcon } from './BackIcon.js';

const OPEN_SHEETS = [
  '.cv-sheetwrap',
  '.ct-sheetwrap',
  '.ag-sheetwrap',
  '.rk-backdrop',
  '.am-backdrop',
  '.pw-back',
  '.aic-back',
  '.lgs-back',
  '.cl-back',
  '.spv-back',
  '.editsheet-backdrop',
  '.lv-root',
  '.mb-light',
  '.cx-dock.open',
  '.stg-panelwrap',
].join(', ');
// Mesmo movimento do deslize do Início para a Equipe.
const SETTLE = 'transform 0.18s cubic-bezier(0.32, 0.72, 0, 1)';
const SETTLE_MS = 180;
const FLICK_PX = 28;
/** Espiada da Início durante o gesto: o palco de verdade aparece por baixo da página que sai. */
export const HOME_PEEK_EVENT = 'secretario:home-peek';

/** O toque começou em algo que já usa o gesto lateral (lista que rola de lado, campo de texto)? */
function ownsHorizontalGesture(target: EventTarget | null): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== document.body) {
    if (node.matches('input, textarea, select, [contenteditable="true"], [data-no-swipe-back]')) {
      return true;
    }
    const style = window.getComputedStyle(node);
    if (
      (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth + 4
    ) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

/** O botão de voltar da página à vista (a Equipe, que tem gesto próprio, não conta). */
function backButton(): HTMLElement | null {
  // Folha ou menu aberto: o gesto não troca de página por baixo dela.
  if (document.querySelector(OPEN_SHEETS)) return null;
  for (const button of document.querySelectorAll<HTMLElement>('.back-btn')) {
    if (!button.closest('[data-no-swipe-back], .swipe-layer')) return button;
  }
  return null;
}

/** Para onde o voltar leva, quando é um link (assim dá para desenhar a tela de destino). */
function backTarget(button: HTMLElement): string | null {
  const href = button instanceof HTMLAnchorElement ? button.getAttribute('href') : null;
  return href && href.startsWith('/') ? href : null;
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** A tela de destino, desenhada com as mesmas rotas do app num endereço fixo. */
function TargetScreen({ path }: { path: string }) {
  const [hook] = useState(() => memoryLocation({ path, static: true }).hook);
  return (
    <Router hook={hook}>
      <Suspense fallback={null}>
        <AppRoutes />
      </Suspense>
    </Router>
  );
}

/**
 * Deslizar para a direita volta uma tela. Segurando o dedo, a tela de destino
 * entra por cima da atual e acompanha o movimento; soltando depois do meio (ou
 * num deslize rápido) ela termina de entrar, senão recua. O gesto aciona o
 * próprio botão de voltar da página, então cada tela volta para onde sempre
 * voltou. Início e Equipe ficam de fora: têm gesto próprio.
 */
export function SwipeBack() {
  const layer = useRef<HTMLDivElement | null>(null);
  const pill = useRef<HTMLDivElement | null>(null);
  const [target, setTarget] = useState<string | null>(null);
  const [frame, setFrame] = useState({ top: 0, bottom: 0 });

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let startAt = 0;
    let tracking = false;
    let decided = false;
    let settling = false;
    let pull = 0;
    let button: HTMLElement | null = null;
    let layered = false;
    // Voltar para a Início: em vez de desenhar um segundo Secretário, a própria página
    // anda para a direita e o palco (que já está ali, parado) aparece por baixo.
    let page: HTMLElement | null = null;
    let width = window.innerWidth;

    const place = (x: number, animate: boolean) => {
      const element = page ?? layer.current;
      if (!element) return;
      element.style.transition = animate ? SETTLE : 'none';
      element.style.transform = `translate3d(${page ? x + width : x}px, 0, 0)`;
    };
    const peekHome = (on: boolean) => {
      document.documentElement.classList.toggle('home-peek', on);
      window.dispatchEvent(new CustomEvent(HOME_PEEK_EVENT, { detail: on }));
    };
    const movePill = (y: number) => {
      const element = pill.current;
      if (!element) return;
      const progress = Math.min(pull / 84, 1);
      element.style.top = `${y}px`;
      element.style.opacity = String(Math.min(progress * 1.4, 1));
      element.style.transform = `translateX(${Math.min(pull * 0.42, 54) - 44}px) scale(${0.7 + progress * 0.3})`;
      element.classList.toggle('armed', pull >= 84);
    };
    const hidePill = () => {
      const element = pill.current;
      if (!element) return;
      element.style.opacity = '0';
      element.style.transform = 'translateX(-44px) scale(0.7)';
      element.classList.remove('armed');
    };
    const reset = () => {
      tracking = false;
      decided = false;
      settling = false;
      layered = false;
      pull = 0;
      button = null;
      if (page) {
        page.style.transition = '';
        page.style.transform = '';
        page = null;
        peekHome(false);
      }
      setTarget(null);
      hidePill();
    };

    const onStart = (event: TouchEvent) => {
      if (settling) return;
      tracking = false;
      decided = false;
      pull = 0;
      const touch = event.touches[0];
      if (event.touches.length !== 1 || !touch) return;
      button = backButton();
      if (!button || ownsHorizontalGesture(event.target)) return;
      startX = touch.clientX;
      startY = touch.clientY;
      startAt = event.timeStamp;
      width = window.innerWidth;
      tracking = true;
    };

    const onMove = (event: TouchEvent) => {
      if (!tracking || settling) return;
      const touch = event.touches[0];
      if (!touch || !button) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        // Só vale se o movimento for claramente para a direita; a rolagem vertical segue normal.
        if (dx <= 0 || Math.abs(dy) > dx * 0.6) {
          tracking = false;
          return;
        }
        decided = true;
        const path = reducedMotion() ? null : backTarget(button);
        const main = path === '/' ? document.querySelector<HTMLElement>('main.app-main') : null;
        if (main) {
          page = main;
          layered = true;
          peekHome(true);
        } else if (path) {
          // A tela de destino ocupa o espaço entre o cabeçalho fixo e a navegação.
          const top = document.querySelector('.psn-top')?.getBoundingClientRect().bottom ?? 0;
          const nav = document.querySelector('.psn-nav')?.getBoundingClientRect();
          setFrame({ top, bottom: nav ? window.innerHeight - nav.top : 0 });
          layered = true;
          setTarget(path);
        }
      }
      pull = Math.max(0, Math.min(dx, width));
      if (layered) place(pull - width, false);
      else movePill(startY);
    };

    const onEnd = (event: TouchEvent) => {
      if (!tracking || !decided || settling) {
        tracking = false;
        return;
      }
      tracking = false;
      const elapsed = Math.max(event.timeStamp - startAt, 1);
      const flick = pull >= FLICK_PX && pull / elapsed > 0.45;
      const goBack = button !== null && (flick || pull >= (layered ? width * 0.4 : 84));
      const chosen = button;
      if (!layered) {
        if (goBack) chosen?.click();
        reset();
        return;
      }
      settling = true;
      place(goBack ? 0 : -width, true);
      window.setTimeout(() => {
        if (goBack) chosen?.click();
        // A tela real assume no próximo desenho; só então a camada sai.
        window.setTimeout(reset, 48);
      }, SETTLE_MS + 20);
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  return (
    <>
      {target && (
        <div
          ref={layer}
          className="swipe-layer"
          style={{
            top: frame.top,
            bottom: frame.bottom,
            transform: 'translate3d(-100%, 0, 0)',
          }}
          aria-hidden="true"
        >
          <div className="app-main swipe-layer-main">
            <TargetScreen path={target} />
          </div>
        </div>
      )}
      <div
        ref={pill}
        className="swipe-back"
        style={{ opacity: 0, transform: 'translateX(-44px) scale(0.7)' }}
        aria-hidden="true"
      >
        <BackIcon />
      </div>
    </>
  );
}
